import { getLocale } from '../../../../utils/locale';

export interface FormatCurrencyOptions {
  readonly notation?: 'standard' | 'compact';
  readonly currencySign?: 'standard' | 'accounting';
  readonly minimumFractionDigits?: number;
  readonly maximumFractionDigits?: number;
  /** Test and Storybook seam only; app code lets `getLocale()` decide. */
  readonly locale?: string | readonly string[];
}

/**
 * Formats an amount in an ISO 4217 currency, or returns null when it cannot.
 *
 * Null — never a guessed symbol — is the contract. `Intl.NumberFormat` throws a RangeError on an
 * absent or malformed currency, and a wrong symbol misstates money, so callers render their own
 * placeholder or omit the amount entirely.
 *
 * Extracted from {@link CurrencyAmount} so surfaces needing a plain string rather than a node —
 * a tooltip's aria-label, for one — do not reimplement this rule and let the two drift.
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  {
    notation = 'standard',
    currencySign = 'standard',
    minimumFractionDigits,
    maximumFractionDigits,
    locale,
  }: FormatCurrencyOptions = {}
): string | null => {
  // `Number('')` and `Number('   ')` are 0, so a blank amount would otherwise
  // render as a real zero.
  const isBlank = typeof amount === 'string' && amount.trim() === '';
  const value = Number(amount);

  if (amount == null || isBlank || Number.isNaN(value)) {
    return null;
  }

  const locales =
    typeof locale === 'string' ? locale : [...(locale ?? getLocale())];

  try {
    return new Intl.NumberFormat(locales, {
      style: 'currency',
      currency: currency as string,
      notation,
      currencySign,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  } catch {
    return null;
  }
};
